import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  IconButton,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TerminalIcon from "@mui/icons-material/Terminal";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { useUI } from "../../Context/UIContext";
import { usePermissions } from "../../Context/permissionsProvider";
import { useVendorIndexPolling } from "../../Context/vendorIndexPollingContext";
import { formatDate, formatValue } from "../../../utils/formatters";
import VendorBusinessTradeNames from "./VendorBusinessTradeNames";
import CatalogMaintenanceShell from "./CatalogMaintenanceShell";
import CatalogSimpleSearch from "./CatalogSimpleSearch";
import CatalogMaintenanceTable from "./CatalogMaintenanceTable";
import CatalogImportLogDialog from "./CatalogImportLogDialog";
import CatalogServiceConfigDialog from "./CatalogServiceConfigDialog";
import CatalogScheduleNextRun from "./CatalogScheduleNextRun";
import CatalogDatabaseStatus from "./CatalogDatabaseStatus";
import ResizableHeaderCell from "./ResizableHeaderCell";
import { useResizableColumns } from "./hooks/useResizableColumns";
import { VENDOR_LIST_COLUMN_WIDTHS } from "./utils/catalogTableLayout";
import useCatalogImportJob from "./hooks/useCatalogImportJob";
import { searchCatalogVendors } from "./services/catalogMappingService";
import CatalogToolbarSelect from "./CatalogToolbarSelect";
import { productsImportedCount, runSummaryTs } from "./utils/catalogFieldLabels";
import {
  isProblematicVendor,
  loadPersistedProblematicFilter,
  persistProblematicFilter,
} from "./utils/vendorProblematicUtils";
import { importJobAlertSeverity } from "./utils/catalogImportLogUtils";
import LegendDialog, { CatalogLegendLink } from "./LegendDialog";
import {
  CatalogImportStatusChip,
  CatalogMappingStateChip,
} from "./utils/catalogStatusDisplay";
import {
  VENDOR_STATO_FILTER_OPTIONS,
  loadPersistedStatoFilter,
  persistStatoFilter,
  vendorStatoFilterValue,
  vendorStatoLabel,
} from "./utils/vendorStatoUtils";
import { CM_TOOLTIPS } from "./utils/tooltipsContent";
import {
  IMPORT_STATUS_LEGEND,
  IMPORT_STATUS_LEGEND_TITLE,
  MAPPING_STATE_LEGEND,
  MAPPING_STATE_LEGEND_TITLE,
} from "./utils/legendsContent";
import { isAnyReindexRunning } from "../../../utils/indexPollingUtils";

const ManutenzioneCataloghi = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showError } = useUI();
  const { isActionVisible } = usePermissions();
  const canServiceConfig = isActionVisible("manutenzioneCataloghi", "serviceConfig");
  const canImport = isActionVisible("manutenzioneCataloghi", "import");
  const { full, vendors } = useVendorIndexPolling();
  const reindexActive = isAnyReindexRunning(full, vendors);
  const reindexingVendorCount = vendors.filter((vendor) => vendor.phase === "INDEXING").length;

  const [searchQuery, setSearchQuery] = React.useState("");
  const [statoFilter, setStatoFilter] = React.useState(loadPersistedStatoFilter);
  const [problematicOnly, setProblematicOnly] = React.useState(loadPersistedProblematicFilter);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [mappingLegendOpen, setMappingLegendOpen] = React.useState(false);
  const [importLegendOpen, setImportLegendOpen] = React.useState(false);
  const [serviceConfigOpen, setServiceConfigOpen] = React.useState(false);

  const showStatoColumn = statoFilter === "STATO:TUTTI";

  const listColumnKeys = useMemo(
    () => (showStatoColumn
      ? ["check", "id", "vendor", "stato", "feed", "mapping", "import", "products", "actions"]
      : ["check", "id", "vendor", "feed", "mapping", "import", "products", "actions"]),
    [showStatoColumn],
  );
  const resizableKeys = useMemo(
    () => (showStatoColumn
      ? ["id", "vendor", "stato", "feed", "mapping", "import", "products", "actions"]
      : ["id", "vendor", "feed", "mapping", "import", "products", "actions"]),
    [showStatoColumn],
  );
  const { getWidth, getCellStyle, startResize, tableMinWidth } = useResizableColumns(listColumnKeys, {
    defaultWidths: VENDOR_LIST_COLUMN_WIDTHS,
    minWidth: 56,
    storageKey: "cm-vendor-list-col-widths",
    resizableKeys,
  });

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await searchCatalogVendors(searchQuery, vendorStatoFilterValue(statoFilter));
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statoFilter, showError]);

  const visibleRows = useMemo(
    () => (problematicOnly ? rows.filter(isProblematicVendor) : rows),
    [rows, problematicOnly],
  );

  const {
    importRunning,
    scheduledImportActive,
    maintenanceLocked,
    progressActive,
    stoppingImport,
    jobStatus,
    scheduleStatus,
    connectedDatabaseName,
    logLines,
    logDialogOpen,
    logContainerRef,
    hasLogs,
    startJob,
    stopJob,
    openLogs,
    closeLogs,
    clearLogs,
    isVendorInRunningJob,
  } = useCatalogImportJob({
    onComplete: loadRows,
    onError: (error) => showError(error.message),
  });

  React.useEffect(() => {
    loadRows();
  }, [loadRows]);

  const toggleVendor = (vendorId) => {
    setSelectedIds((prev) =>
      prev.includes(vendorId) ? prev.filter((id) => id !== vendorId) : [...prev, vendorId],
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === visibleRows.length) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(visibleRows.map((row) => row.vendor_id));
  };

  const beginImport = async (vendorIds, importType = "manual_selected") => {
    try {
      await startJob(vendorIds, { importType });
    } catch (error) {
      showError(error.message);
    }
  };

  const handleImportSelected = () => {
    if (selectedIds.length === 0) {
      showError("Seleziona almeno un vendor");
      return;
    }
    beginImport(selectedIds, "manual_selected");
  };
  const handleImportVendor = (vendorId) => beginImport([vendorId], "manual_single");

  const handleImportAll = async () => {
    try {
      await startJob(null, { importType: "manual_all" });
    } catch (error) {
      showError(error.message);
    }
  };

  const operationsDisabled = importRunning || maintenanceLocked;

  const handleStopScheduledImport = async () => {
    try {
      await stopJob();
    } catch (error) {
      showError(error.message);
    }
  };

  return (
    <CatalogMaintenanceShell>
      <div className="cm-stack-header cm-page-title-row">
        <Typography className="cm-title" component="h1">
          Manutenzione Cataloghi
        </Typography>
        <div className="cm-page-title-meta">
          <CatalogDatabaseStatus databaseName={connectedDatabaseName} />
          <CatalogScheduleNextRun schedule={scheduleStatus} />
        </div>
      </div>

      {reindexActive && (
        <Alert className="cm-spacer-md" severity="info">
          {full?.running
            ? `Reindex completo in corso (${full.percentage ?? 0}%).`
            : `Reindex vendor in corso (${reindexingVendorCount} attivi).`}
        </Alert>
      )}

      <div className="cm-panel cm-main-toolbar cm-tab-toolbar">
        <div className="cm-toolbar-search cm-tab-toolbar-body">
          <CatalogSimpleSearch
            value={searchQuery}
            placeholder="Cerca vendor per nome o ID"
            buttonLabel="Cerca"
            buttonTooltip={CM_TOOLTIPS.search}
            onSearch={setSearchQuery}
          />
        </div>
        <div className="cm-main-toolbar-filters">
          <CatalogToolbarSelect
            value={statoFilter}
            disabled={maintenanceLocked}
            onChange={(nextValue) => {
              setStatoFilter(nextValue);
              persistStatoFilter(nextValue);
              setSelectedIds([]);
            }}
            options={VENDOR_STATO_FILTER_OPTIONS.map((option) => ({
              value: option.value,
              label: option.value,
            }))}
            ariaLabel="Filtro stato vendor"
            title={CM_TOOLTIPS.statoFilter}
          />
          <Button
            className="cm-toolbar-btn cm-toolbar-problematic"
            variant={problematicOnly ? "contained" : "outlined"}
            title={CM_TOOLTIPS.soloProblematici}
            disabled={maintenanceLocked}
            onClick={() => {
              setProblematicOnly((previous) => {
                const next = !previous;
                persistProblematicFilter(next);
                return next;
              });
              setSelectedIds([]);
            }}
          >
            SOLO PROBLEMATICI
          </Button>
        </div>
        <div className="cm-tab-toolbar-actions">
          {scheduledImportActive && (
            <Button
              className="cm-toolbar-btn"
              variant="contained"
              color="error"
              startIcon={<StopCircleOutlinedIcon />}
              onClick={handleStopScheduledImport}
              disabled={stoppingImport}
              title="Arresta import schedulato in corso"
            >
              Arresta import schedulato
            </Button>
          )}
          <Button className="cm-toolbar-btn" variant="outlined" startIcon={<SettingsOutlinedIcon />} onClick={() => setServiceConfigOpen(true)} disabled={!canServiceConfig} title="Configurazione servizio import">
            Config servizio
          </Button>
          <Button className="cm-toolbar-btn" variant="outlined" startIcon={<RefreshIcon />} onClick={loadRows} disabled={operationsDisabled} title={CM_TOOLTIPS.aggiornaLista}>
            Aggiorna
          </Button>
          <Button className="cm-toolbar-btn" variant="outlined" startIcon={<TerminalIcon />} onClick={openLogs} disabled={!hasLogs && !scheduledImportActive} title={CM_TOOLTIPS.logs}>
            Logs
          </Button>
          <Button className="cm-toolbar-btn" variant="outlined" startIcon={<PlayArrowIcon />} onClick={handleImportAll} disabled={operationsDisabled || !canImport} title={CM_TOOLTIPS.importaTutti}>
            Importa tutti
          </Button>
          <Button className="cm-toolbar-btn" variant="contained" startIcon={<PlayArrowIcon />} onClick={handleImportSelected} disabled={operationsDisabled || selectedIds.length === 0 || !canImport} title={CM_TOOLTIPS.importaSelezionati}>
            Importa ({selectedIds.length})
          </Button>
        </div>
      </div>

      {scheduledImportActive && (
        <Alert
          className="cm-spacer-md"
          severity="warning"
          action={(
            <Button variant="text" size="small" onClick={openLogs} title={CM_TOOLTIPS.mostraLog}>
              Mostra log
            </Button>
          )}
        >
          Import schedulato in corso
          {jobStatus?.processed != null && jobStatus?.total != null ? ` — ${jobStatus.processed}/${jobStatus.total} vendor` : ""}
          {jobStatus?.current_vendor_name ? ` — ${jobStatus.current_vendor_name}` : ""}
        </Alert>
      )}

      {importRunning && !scheduledImportActive && (
        <Alert className="cm-spacer-md" severity="info" action={(
          <Button variant="text" size="small" onClick={openLogs} title={CM_TOOLTIPS.mostraLog}>
            Mostra log
          </Button>
        )}>
          Import in corso
          {jobStatus?.processed != null && jobStatus?.total != null ? ` — ${jobStatus.processed}/${jobStatus.total} vendor` : ""}
          {jobStatus?.current_vendor_name ? ` — ${jobStatus.current_vendor_name}` : ""}
        </Alert>
      )}

      {!importRunning && jobStatus?.status && (
        <Alert className="cm-spacer-md" severity={importJobAlertSeverity(jobStatus.status)}>
          Ultimo job: {jobStatus.status}
          {jobStatus.processed != null && jobStatus.total != null ? ` (${jobStatus.processed}/${jobStatus.total})` : ""}
        </Alert>
      )}

      <CatalogMaintenanceTable
        tableLoading={loading}
        tableLayout="fixed"
        tableMinWidth={tableMinWidth}
        head={(
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" style={getCellStyle("check")}>
                <Checkbox
                  checked={visibleRows.length > 0 && selectedIds.length === visibleRows.length}
                  indeterminate={selectedIds.length > 0 && selectedIds.length < visibleRows.length}
                  onChange={toggleSelectAll}
                  disabled={maintenanceLocked}
                />
              </TableCell>
              <ResizableHeaderCell columnKey="id" width={getWidth("id")} onResizeStart={startResize}>ID</ResizableHeaderCell>
              <ResizableHeaderCell columnKey="vendor" width={getWidth("vendor")} onResizeStart={startResize}>Vendor</ResizableHeaderCell>
              {showStatoColumn && (
                <ResizableHeaderCell columnKey="stato" width={getWidth("stato")} onResizeStart={startResize}>Stato</ResizableHeaderCell>
              )}
              <ResizableHeaderCell columnKey="feed" width={getWidth("feed")} onResizeStart={startResize}>Feed URL</ResizableHeaderCell>
              <ResizableHeaderCell columnKey="mapping" width={getWidth("mapping")} onResizeStart={startResize}>
                <span className="cm-th-with-legend">
                  Mapping
                  <CatalogLegendLink onClick={() => setMappingLegendOpen(true)} title={MAPPING_STATE_LEGEND_TITLE} />
                </span>
              </ResizableHeaderCell>
              <ResizableHeaderCell columnKey="import" width={getWidth("import")} onResizeStart={startResize}>
                <span className="cm-th-with-legend">
                  Ultimo import
                  <CatalogLegendLink onClick={() => setImportLegendOpen(true)} title={IMPORT_STATUS_LEGEND_TITLE} />
                </span>
              </ResizableHeaderCell>
              <ResizableHeaderCell columnKey="products" width={getWidth("products")} onResizeStart={startResize} className="cm-align-right">Prodotti</ResizableHeaderCell>
              <ResizableHeaderCell columnKey="actions" width={getWidth("actions")} onResizeStart={startResize} className="cm-align-right">Azioni</ResizableHeaderCell>
            </TableRow>
          </TableHead>
        )}
        body={(
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={showStatoColumn ? 9 : 8}>
                  <Typography className="cm-muted-text" align="center" sx={{ py: 3 }}>
                    Caricamento...
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && visibleRows.map((row) => (
              <TableRow key={row.vendor_id} selected={selectedIds.includes(row.vendor_id)}>
                <TableCell padding="checkbox" style={getCellStyle("check")}>
                  <Checkbox checked={selectedIds.includes(row.vendor_id)} onChange={() => toggleVendor(row.vendor_id)} disabled={maintenanceLocked} />
                </TableCell>
                <TableCell style={getCellStyle("id")}>{row.vendor_id}</TableCell>
                <TableCell style={getCellStyle("vendor")}>
                  <VendorBusinessTradeNames vendor={row} />
                </TableCell>
                {showStatoColumn && (
                  <TableCell style={getCellStyle("stato")}>{vendorStatoLabel(row.stato)}</TableCell>
                )}
                <TableCell style={getCellStyle("feed")}>
                  <Typography noWrap title={row.feed_url || ""}>
                    {row.feed_url || "—"}
                    {row.feed_requires_auth ? " [auth]" : ""}
                  </Typography>
                </TableCell>
                <TableCell style={getCellStyle("mapping")}>
                  <CatalogMappingStateChip mappingState={row.mapping_status} />
                </TableCell>
                <TableCell style={getCellStyle("import")}>
                  <Stack spacing={0.5}>
                    {isVendorInRunningJob(row.vendor_id) ? (
                      <CatalogImportStatusChip status="RUNNING" />
                    ) : (
                      <>
                        <CatalogImportStatusChip status={row.last_run_state} />
                        <Typography className="cm-muted-text" variant="caption">{formatDate(runSummaryTs(row.last_run_summary))}</Typography>
                      </>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right" style={getCellStyle("products")}>
                  {productsImportedCount(row.last_run_summary) || row.product_count || 0}
                </TableCell>
                <TableCell align="right" style={getCellStyle("actions")}>
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <IconButton size={isMobile ? "small" : "medium"} onClick={() => handleImportVendor(row.vendor_id)} disabled={operationsDisabled} title={CM_TOOLTIPS.importSingolo}>
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                    <IconButton size={isMobile ? "small" : "medium"} onClick={() => navigate(`/manutenzione-cataloghi/operazioni-vendor/${row.vendor_id}`)} disabled={maintenanceLocked} title={CM_TOOLTIPS.mappingCatalogo}>
                      <SettingsOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!loading && visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={showStatoColumn ? 9 : 8}>
                  <Typography className="cm-muted-text" align="center" sx={{ py: 4 }}>
                    {problematicOnly
                      ? "Nessun vendor problematico con i filtri attuali."
                      : "Nessun vendor con feed URL configurato."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        )}
      />
      <LegendDialog
        open={mappingLegendOpen}
        onClose={() => setMappingLegendOpen(false)}
        title={MAPPING_STATE_LEGEND_TITLE}
        items={MAPPING_STATE_LEGEND}
      />
      <LegendDialog
        open={importLegendOpen}
        onClose={() => setImportLegendOpen(false)}
        title={IMPORT_STATUS_LEGEND_TITLE}
        items={IMPORT_STATUS_LEGEND}
      />
      <CatalogImportLogDialog
        open={logDialogOpen}
        onClose={closeLogs}
        onClearLogs={clearLogs}
        onStopImport={stopJob}
        stoppingImport={stoppingImport}
        logLines={logLines}
        logContainerRef={logContainerRef}
        jobStatus={jobStatus}
        importRunning={progressActive}
      />
      <CatalogServiceConfigDialog
        open={serviceConfigOpen}
        onClose={() => setServiceConfigOpen(false)}
      />
    </CatalogMaintenanceShell>
  );
};

export default ManutenzioneCataloghi;
